import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { FIELD_CHROME, type FieldVariant } from '@/lib/field-chrome';
import { useFieldVariant } from '@/components/ui/field-variant';

const inputVariants = cva(
  'flex w-full text-foreground file:border-0 file:bg-transparent file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 dark:text-neutral-100 dark:placeholder:text-neutral-400',
  {
    variants: {
      variant: {
        solid: `${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusVisible}`,
        glass: `${FIELD_CHROME.glass.base} ${FIELD_CHROME.glass.focusVisible}`,
      },
      uiSize: {
        sm: 'h-10 rounded-lg px-3 text-sm',
        md: 'h-11 rounded-xl px-4 text-sm',
        lg: 'h-12 rounded-2xl px-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'solid',
      uiSize: 'md',
    },
  }
);

const PREFIX_PILL_SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-10 rounded-lg',
  md: 'h-11 rounded-xl',
  lg: 'h-12 rounded-2xl',
};

type InputProps = Omit<React.ComponentProps<'input'>, 'prefix'> &
  Omit<VariantProps<typeof inputVariants>, 'variant'> & {
    /**
     * Visual variant. Defaults to the nearest `FieldVariantProvider`, or
     * `'solid'` if none is present.
     */
    variant?: FieldVariant;
    /**
     * Optional element rendered inside the input on the left side (e.g. an
     * icon or currency symbol). Automatically adds left padding so the text
     * does not overlap. Pointer events are disabled so clicks pass through.
     */
    icon?: React.ReactNode;
    /**
     * Optional element rendered inside the input on the right side (e.g. a
     * password visibility toggle or clear button). Unlike `icon`, this is
     * interactive — clicks are not blocked. Automatically adds right padding.
     */
    trailing?: React.ReactNode;
    /**
     * Optional element rendered as a separate styled pill adjacent to the
     * input (e.g. a URL prefix like `/e/` before a slug field). The pill
     * shares the input's border, shadow, and corner radius.
     */
    prefix?: React.ReactNode;
  };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, uiSize, variant, icon, trailing, prefix, ...props }, ref) => {
    const resolvedVariant = useFieldVariant(variant);
    const sizeKey = uiSize ?? 'md';

    const needsWrapper = !!icon || !!trailing;

    const inputEl = (
      <input
        type={type}
        className={cn(
          inputVariants({ variant: resolvedVariant, uiSize }),
          icon && 'pl-11',
          trailing && 'pr-11',
          className
        )}
        ref={ref}
        {...props}
      />
    );

    const inputWithDecorations = needsWrapper ? (
      <div className="relative w-full">
        {icon && (
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
            {icon}
          </span>
        )}
        {inputEl}
        {trailing && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center text-muted-foreground">
            {trailing}
          </span>
        )}
      </div>
    ) : (
      inputEl
    );

    if (!prefix) return inputWithDecorations;

    const pillChromeClass =
      resolvedVariant === 'glass' ? FIELD_CHROME.glass.base : FIELD_CHROME.solid.base;

    return (
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'inline-flex shrink-0 items-center px-3 text-sm text-muted-foreground dark:text-neutral-400',
            pillChromeClass,
            PREFIX_PILL_SIZE[sizeKey]
          )}
        >
          {prefix}
        </div>
        {inputWithDecorations}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
